import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
  lazy,
  Suspense,
} from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import GTM from "@src/services/gtm"
import PageHelmet, { PageSeoPropTypes } from "@src/components/page-helmet"
import { normalizeTitle, generateMeta } from "@src/utils/page-data"
import PrimaryLayout from "@src/layouts/primary"
import ResponsiveSection from "@src/components/container-responsive-section"
import SingleTourHeader from "@src/components/page-header-single-tour"
import HtmlContent from "@src/components/core-value-html"
import ReadMoreText from "@src/components/core-value-read-more-text"
import HotelTile, {
  HotelTilePropTypes,
} from "@src/components/content-hotel-tile"
import { useTourBreadcrumbs } from "@src/hooks/useTourBreadcrumbs"
import { ImagePropTypes } from "@src/components/core-image"
import { PricePropTypes } from "@src/graphql-fragments/prices"
import SocialProofReasons from "@src/components/content-social-proof-reasons-v2"
import TourOperatorReviewsService from "@src/services/reviews/tour-operator-reviews"
import { formatDate } from "@src/utils/dates"
import { useTourContinents } from "@src/hooks/useTourContinents"
import { getRegionChildTypeName } from "@src/utils/getRegionChildTypeName"
import BreadcrumbsV2 from "@src/components/nav-content-breadcrumbs-v2"
import generateBreadcrumbList from "@src/utils/structured-data/generateBreadcrumbList"
import generateProduct from "@src/utils/structured-data/generateProduct"
import { SITE_DOMAIN } from "@src/utils/constants"
import { convertToMonths } from "@src/utils/convert-number-to-month"
import { TOUR_DIFFICULTY_OPTIONS } from "@src/utils/constants/tour-list-options"
import StickyBookingPriceCTA from "@src/components/content-tour-sticky-booking-price-cta"
import HappinessGuarantee from "@src/components/content-tour-happiness-guarantee"
import { LayerContext } from "@src/context/layer-context"
import { instantBookings } from "@src/utils/constants/instant-booking-tours"
import { LocaleContext } from "@src/context/locale-context"
import ModalContainer from "@src/components/container-modal"
import QuestionForm from "@src/components/forms/form-question/form-question"
import FormContext from "@src/components/forms/form-context"
import { TOUR_CATEGORY_ICONS } from "@src/utils/constants/tour-category-icons"
import { TOUR_REGION_CATEGORY_INCLUSION_IDS } from "@src/utils/constants/region-activity-inclusions"
import { screen } from "@src/styles/theme"
import { useMediaPartners } from "@src/hooks/useMediaPartners"
import { GroupTrips } from "@src/components/content-tour-group-trips"
import { GroupTripForm } from "@src/components/forms/content-tour-group-trips"
import HotjarScript from "@src/scripts/hotjar"
import Header from "@src/components/indy-tour-page/header"
import InfoSideMenu from "@src/components/indy-tour-page/info-side-menu"
import InfoSideMenuPart2 from "@src/components/indy-tour-page/info-side-menu-part2"
import TourDescription from "@src/components/indy-tour-page/tour-description"
import TripHighlights from "@src/components/indy-tour-page/trip-highlights"
import MapAndTravellers from "@src/components/indy-tour-page/map-and-travellers"
import DayByDay from "@src/components/indy-tour-page/day-by-day"
const Reviews = lazy(() => import("@src/components/indy-tour-page/reviews"))
const DatesAndPrices = lazy(
  () => import("@src/components/indy-tour-page/dates-and-prices"),
)
const Included = lazy(() => import("@src/components/indy-tour-page/included"))
const Accommodation = lazy(
  () => import("@src/components/indy-tour-page/accommodation"),
)
const FaqsSection = lazy(
  () => import("@src/components/indy-tour-page/faqs-section"),
)
const WhyChoose = lazy(
  () => import("@src/components/indy-tour-page/why-choose"),
)

import WeAre from "@src/components/indy-tour-page/we-are"
import { checkTourTnC } from "@src/utils/tour-tnc"
import NonStandardCancellation from "@src/components/indy-tour-page/non-standard.terms"
const ResponsiveSectionDark = styled(ResponsiveSection)`
  background: #0c0c0c;
`

const WhyResponsiveSection = styled(ResponsiveSection)`
  background: ${props => props.theme.secondary900};
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TourDescriptionHolder = styled.span`
  @media (max-width: 1024px) {
    display: none;
  }
`

// Ask A Question
const QuestionModal = styled(ModalContainer)`
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
`

// Main Layout
const AboveFold = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 24px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  padding: 16px;
  & > .seen-in {
    margin: 16px 0;
    & h2 {
      font-family: system-ui;
      font-size: 26px;
      font-weight: 700;
      line-height: 32px;
      letter-spacing: 0.5px;
      text-align: center;
      color: #1f1f23; // Natasha Design
    }
    & > div {
      background: #1f1f23; // Natasha Design
      padding: 20px 10px;
      display: flex;
      align-items: center;
      justify-content: space-around;
      & > .lazyload-wrapper {
        width: 90px;
        & img {
          filter: brightness(0) invert(1);
        }
      }
    }
  }
  @media only screen and (${props => props.theme.screen.small.min}) {
    & > .seen-in {
      display: none;
    }
  }
  @media only screen and (${props => props.theme.screen.medium.min}) {
    max-width: ${props => props.theme.screen.medium.contentMaxWidths}px;
    grid-template-columns: auto 335px;
    padding: 16px 0;

    & > .breadcrumbs {
      grid-row: 1 / 2;
      grid-column: 1 / 3;
    }
    & > .tour-title-container {
      grid-row: 2 / 3;
      grid-column: 1 / 3;
    }
    & > .about-tour-container {
      grid-row: 4 / 5;
      grid-column: 1 / 3;
    }
  }

  @media only screen and (${props => props.theme.screen.large.min}) {
    max-width: ${props => props.theme.screen.large.contentMaxWidths}px;
  }

  @media only screen and (${props => props.theme.screen.desktop.min}) {
    max-width: ${props => props.theme.screen.desktop.contentMaxWidths}px;
  }
`
const RelatedToursSection = styled(ResponsiveSection)`
  margin-bottom: 40px;
`
const PageTemplate = ({
  data: { tenTours: data },
  pageContext: { pageUri, skipForBottomSeo, replaceURLs },
}) => {
  const {
    tour: {
      images,
      title,
      slug: tourSlug,
      spotsRemaining,
      tourHighlights,
      seoText,
      seo: pageSeo,
      tourDifficulty,
      price,
      priceFooter,
      priceHeader,
      pricingTablesNew,
      dayofweekPrices,
      fixedDates,
      groupTypes,
      tourIcons,
      tourMonths,
      itinerary,
      accommodationText,
      accommodationGroups,
      tourIncluded,
      faqs,
      tourOperator,
      tourCategory,
      tourRegion,
      map,
      tourTnC,
      tourType,
    },
  } = data
  const [showHP, setShowHP] = useState(false)
  const [showGT, setShowGT] = useState(false)
  const [isQuestionOpen, setIsQuestionOpen] = useState(false)
  const [isGroupTripOpen, setIsGroupTripOpen] = useState(false)
  const [bookingURL, setBookingURL] = useState(`/bookings/tour/${tourSlug}/`)
  const { headerBreadcrumbs } = useTourBreadcrumbs(tourRegion.id)
  const { currency, country } = useContext(LocaleContext)
  const tourContinents = useTourContinents()
  const mediaPartners = useMediaPartners().filter(partner =>
    ["National Geographic", "Fodor’s", "The Guardian"].includes(partner.title),
  )
  const findCountry = region => {
    if (region.name === "Antarctica") {
      return region.name
    }
    const parent = region?.regionParent?.node
    const grandparent = parent?.regionGrandparent

    if (parent && grandparent && grandparent.node) {
      // If parent has grandparent, then parent is the country
      return parent.name
    }

    // If this region is already the country
    const hasParent = !!region?.regionParent?.node
    const hasGrandparent = !!region?.regionParent?.node?.regionGrandparent?.node

    if (hasParent && !hasGrandparent) {
      return region.name
    }

    return null
  }
  // Click Refs
  // const reviewsRef = useRef()
  // Review States & Ref
  const [reviews, setReviews] = useState({
    overall: tourOperator && tourOperator.reviews.overall,
    numReviews: tourOperator && { ...tourOperator.reviews.counts },
    content: [],
  })

  const terms = checkTourTnC(tourOperator?.operatorTnC, tourTnC)
  const reviewsLoaded = useRef({ items: [], lastPage: 0, loadCount: 0 })
  // Open and close layers based on showHP state
  const { open: openLayer, close: closeLayer } = useContext(LayerContext)

  // Ratings
  const processRatingsData = useCallback(
    (overall, counts) =>
      setReviews(prev => ({ ...prev, overall: overall, numReviews: counts })),
    [setReviews],
  )
  const loadRatings = useCallback(() => {
    TourOperatorReviewsService.getOperatorRatings(tourOperator.id)
      .then(data =>
        processRatingsData(data.items.length && data.items[0][1], data.counts),
      )
      .catch(error => console.log(error))
  }, [tourOperator, processRatingsData])
  useEffect(() => loadRatings(), [loadRatings])
  // Reviews
  const processReviewsData = useCallback(
    (reviewsData, reset) => {
      if (reset) {
        reviewsLoaded.current.items = reviewsData.items || []
        // reviewsLoaded.current.loadCount = 0
      } else if (reviewsData.items && reviewsData.items.length) {
        reviewsLoaded.current.items = reviewsLoaded.current.items.concat(
          reviewsData.items,
        )
      }
      // reviewsLoaded.current.loadCount++
      reviewsLoaded.current.lastPage = reviewsData.page
      // Required to only render first 4 reviews on page load
      // const content =
      //   reviewsLoaded.current.loadCount === 1
      //     ? reviewsLoaded.current.items.slice(0, 4)
      //     : reviewsLoaded.current.items
      const content = reviewsLoaded.current.items

      // State change to render reviews & read more reviews button
      setReviews(prev => ({ ...prev, content }))
    },
    [setReviews, reviewsLoaded],
  )
  const loadMoreReviews = useCallback(
    reset => {
      TourOperatorReviewsService.getOperatorReviews(tourOperator.id, {
        page: reset ? 1 : reviewsLoaded.current.lastPage + 1,
        perPage: 200,
      }).then(reviewsData => {
        if (reviewsData) {
          const { items, ...paginationData } = reviewsData
          const reviewData = items
            .filter(item => item.text && item.text.trim().length !== 0)
            .map(filteredItem => ({
              name: filteredItem.user?.name,
              rating:
                filteredItem.ratings.length &&
                filteredItem.ratings[0].length &&
                filteredItem.ratings[0][1],
              date:
                filteredItem.time &&
                formatDate(new Date(filteredItem.time), "MMMM, yyyy"),
              content: filteredItem.text,
            }))
          processReviewsData({ items: reviewData, ...paginationData }, reset)
        }
      })
    },
    [tourOperator, processReviewsData, reviewsLoaded],
  )

  useEffect(() => {
    if (tourOperator && tourOperator.id) {
      loadMoreReviews(true)
    }
  }, [tourOperator, loadMoreReviews])

  const AskQuestionModal = (
    <QuestionModal
      id="ask-question-modal"
      isOpen={isQuestionOpen}
      onClose={() => setIsQuestionOpen(false)}
      title={title}
    >
      <p>
        Chat with us on our{" "}
        <a
          href="/contact/"
          target="_blank"
          aria-label="Contact page, opens a new tab"
        >
          contact page
        </a>{" "}
        or fill out the form below!
      </p>
      <FormContext>
        <QuestionForm
          name="tour-question-zoho"
          pageName={title}
          pagePath={pageUri}
          operator={tourOperator.name}
          productPrice={price}
          onClose={() => setIsQuestionOpen(false)}
        />
      </FormContext>
    </QuestionModal>
  )
  const GroupTripModal = (
    <QuestionModal
      id="group-trip-modal"
      $isOpen={isGroupTripOpen}
      onClose={() => setIsGroupTripOpen(false)}
      title={title}
    >
      <FormContext>
        <GroupTripForm
          pageName={title}
          pagePath={pageUri}
          operator={tourOperator.name}
          onClose={() => setIsGroupTripOpen(false)}
        />
      </FormContext>
    </QuestionModal>
  )

  const tabData = useMemo(() => {
    const result = [
      {
        title: "Accommodation",
        className: "accommodation",
        content: (
          <>
            <h3 className="green-header">{`Accommodation for ${title}`}</h3>
            <HtmlContent
              html={accommodationText.replace(/<h2>(.*?)<\/h2>/, "")}
              prices={{ showAbbr: "after-full-size" }}
            />
            {accommodationGroups &&
              accommodationGroups.length &&
              accommodationGroups.map((accommodationGroup, index) => (
                <ReadMoreText
                  key={index}
                  seeMoreHeader={accommodationGroup.listTitle}
                  variant="fancy"
                  buttonTextObject={{
                    toOpen: { text: "See more", icon: "circle-plus" },
                    toClose: { text: "See less", icon: "circle-minus" },
                  }}
                  buttonTop
                  hideAll
                >
                  {accommodationGroup.accommodations.map(
                    ({ hotel, nightNumber }, index) => (
                      <HotelTile
                        key={index}
                        hotel={hotel}
                        nightNumber={nightNumber}
                      />
                    ),
                  )}
                </ReadMoreText>
              ))}
          </>
        ),
        icon: "v2-thick-Tent",
      },
    ]
    return result
  }, [accommodationText, accommodationGroups, title])

  // const scrollToElement = elementName => {
  //   scroller.scrollTo(elementName, { smooth: true, offset: -125 })
  // }

  // const onRatingsClick = event => {
  //   event.preventDefault()
  //   scrollToElement("tour-operator-reviews")
  //   reviewsRef.current.click()
  // }
  // Happiness Guarantee
  const handleHPClick = () => {
    setShowHP(prev => !prev)
  }
  // const handleGTClick = () => {
  //   setShowGT(prev => !prev)
  // }
  const handleHPClose = useCallback(() => {
    setShowHP(false)
  }, [setShowHP])
  const handleGTClose = useCallback(() => {
    setShowGT(false)
  }, [setShowGT])

  useEffect(() => {
    if (showHP) {
      openLayer({
        layerId: "happiness-promise",
        onClose: handleHPClose,
        level: 2,
      })
    } else {
      closeLayer("happiness-promise")
    }
  }, [showHP, openLayer, handleHPClose, closeLayer])
  useEffect(() => {
    if (showGT) {
      openLayer({
        layerId: "group-trips",
        onClose: handleGTClose,
        level: 2,
      })
    } else {
      closeLayer("group-trips")
    }
  }, [showGT, openLayer, handleGTClose, closeLayer])

  // Tour Icons
  const revisedTourIcons = useMemo(() => {
    const result = [...tourIcons]

    if (groupTypes && groupTypes.nodes && groupTypes.nodes.length) {
      const tripType = result.find(item => item.icon === "distance")
      if (tripType && tripType.label) {
        groupTypes.nodes.forEach(type => {
          if (!tripType.label.includes(type.name))
            tripType.label += `, ${type.name}`
        })
      }
    }
    if (tourMonths && tourMonths.length) {
      result.splice(1, 0, {
        label: convertToMonths(tourMonths),
        icon: "v2-calendar",
      })
    }
    if (tourCategory) {
      result.splice(2, 0, {
        label: tourCategory.name,
        icon: TOUR_CATEGORY_ICONS[tourCategory.name] || "v2-calendar",
      })
    }
    if (tourDifficulty) {
      const label = TOUR_DIFFICULTY_OPTIONS.find(
        item => item.value === tourDifficulty,
      ).text
      result.push({
        label: `${label} (${tourDifficulty}/5)`,
        icon: "v2-thick-Fire",
      })
    }
    return result
  }, [tourIcons, tourCategory, tourMonths, tourDifficulty, groupTypes])

  // Parent and grandparent
  const ancestors = useMemo(() => {
    const result = { text: "All Tours", url: "/tours/" }
    const { regionParent } = tourRegion
    if (regionParent && regionParent.node) {
      result.url = regionParent.node.uri
      result.text = regionParent.node.name
      if (
        regionParent.node.regionGrandparent &&
        regionParent.node.regionGrandparent.node
      ) {
        result.text += `, ${regionParent.node.regionGrandparent.node.name}`
      }
    }
    return result
  }, [tourRegion])
  // Bottom Seo
  let bottomSeoData = useMemo(() => {
    let result = []

    const { regionCategories, regionChildren, regionParent, ...region } =
      tourRegion

    const regionSiblings =
      regionParent &&
      regionParent.node &&
      regionParent.node.regionSiblings &&
      regionParent.node.regionSiblings.nodes.filter(
        sibling => sibling.name !== region.name,
      )

    // Currently the 'region' is assigned to the parent so the 'relations' are one above what they should be.
    // Parent = Grandparent, region = parent, children = siblings (including self)
    // Example: A tour in Amalfi is actually assigned to Italy
    // This can be fixed by manually updated the primary region for tours in the back-end on 10a-tours.
    if (regionSiblings && regionSiblings.length) {
      const siblingRegions = regionSiblings.map(sibling => ({
        name: `${sibling.name} Tours`,
        link: sibling.uri,
      }))

      result.push({
        title: `Tours in ${regionParent.node.name} by ${getRegionChildTypeName(
          regionParent.node.name,
          tourContinents,
        )}`,
        backlink: {
          text: `Back to ${regionParent.node.name} Tours`,
          link: regionParent.node.uri,
        },
        items: siblingRegions,
      })
      // Other primary activity tours in region
      if (tourCategory) {
        // Only want the top-level activity. Ex: Bike for e-bike or road bike
        const primaryCategoryId = tourCategory.parentId || tourCategory.id
        const primaryCategory = regionCategories.nodes.find(
          category => category.id === primaryCategoryId,
        )
        if (
          primaryCategory &&
          TOUR_REGION_CATEGORY_INCLUSION_IDS.includes(primaryCategory.id)
        ) {
          const siblingsWithSameCategory = regionSiblings
            .filter(
              sibling =>
                sibling.siblingRegionTourCategories &&
                sibling.siblingRegionTourCategories.nodes.length &&
                !!sibling.siblingRegionTourCategories.nodes.find(
                  category => category.id === primaryCategoryId,
                ) &&
                !skipForBottomSeo.includes(
                  `/tours/${sibling.slug}-${primaryCategory.slug}/`,
                ),
            )
            .map(sibling => ({
              name: `${sibling.name} ${primaryCategory.name} Tours`,
              link:
                replaceURLs[
                  `/tours/${sibling.slug}-${primaryCategory.slug}/`
                ] || `/tours/${sibling.slug}-${primaryCategory.slug}/`,
            }))

          if (siblingsWithSameCategory && siblingsWithSameCategory.length) {
            result.push({
              title: `${primaryCategory.name} Tours in ${
                regionParent.node.name
              } by ${getRegionChildTypeName(
                regionParent.node.name,
                tourContinents,
              )}`,
              backlink: {
                text: `Back to ${primaryCategory.name} Tours`,
                link: primaryCategory.uri.replace(/^\/activity\//, "/tours/"),
              },
              items: siblingsWithSameCategory,
            })
          }
        }
      }
    }
    // Other tours in parent
    if (regionChildren && regionChildren.nodes.length) {
      const siblingRegions = regionChildren.nodes.map(child => ({
        name: `${child.name} Tours`,
        link: child.uri,
      }))
      result.push({
        title: `Tours in ${region.name} by ${getRegionChildTypeName(
          region.name,
          tourContinents,
        )}`,
        backlink: {
          text: `Back to ${region.name} Tours`,
          link: region.uri,
        },
        items: siblingRegions,
      })
    }

    // Tours by activity in parent
    if (regionCategories && regionCategories.nodes.length) {
      const filteredCategories = regionCategories.nodes.filter(
        category =>
          TOUR_REGION_CATEGORY_INCLUSION_IDS.includes(category.id) &&
          !skipForBottomSeo.includes(`/tours/${region.slug}-${category.slug}/`),
      )
      const categories =
        filteredCategories &&
        filteredCategories.map(category => ({
          name: `${category.name} Tours in ${region.name}`,
          link:
            replaceURLs[`/tours/${region.slug}-${category.slug}/`] ||
            `/tours/${region.slug}-${category.slug}/`,
        }))
      result.push({
        title: `${region.name} Tours by Activity `,
        backlink: {
          text: `Back to ${region.name} Tours`,
          link: region.uri,
        },
        items: categories,
      })
    }
    return result
  }, [tourRegion, tourCategory, tourContinents, skipForBottomSeo, replaceURLs])
  GTM.dataLayerPushPageInfo({
    template: "tour-single",
  })
  // Responsive Image Setting
  let featureImage = {}
  if (
    images &&
    images.feature &&
    images.feature.sizes &&
    images.feature.sizes.length
  ) {
    const { feature } = images
    const { alt, sizes } = feature
    featureImage.alt = alt
    let srcSet = ""
    const srcSetImages = sizes.filter(image =>
      image.name.includes("Tour Feature Image"),
    )
    srcSetImages.forEach((image, index) => {
      if (index === 0) {
        featureImage.src = image.src
      }
      srcSet += `${image.src} ${image.width}w${
        index !== srcSetImages.length - 1 ? `,` : ""
      }`
    })
    const viewportWidth = "100vw - (100vw - 100%)"
    featureImage.sizes = `(max-width: 392px) calc(${viewportWidth} - 32px),(min-width:393px) calc(${viewportWidth} - 32px - 16px - 125px),(${screen.tablet.min}) calc(${viewportWidth} - 32px),(${screen.small.min}) calc(${viewportWidth} - 32px - 16px - 150px),(${screen.medium.min}) 615px, 755px`
    featureImage.srcSet = srcSet
  }

  // Structured Data
  const rawStructuredData = {
    name: title,
    description: pageSeo.metaDesc,
    ratings: reviews &&
      reviews.overall && {
        // Required - name, value, count
        name: "Overall Rating",
        value: reviews.overall,
        count: reviews.numReviews && reviews.numReviews.total,
        best: 5,
        worst: 0,
      },
    image: images && images.feature.src,
    price: price &&
      price.amount &&
      price.currency &&
      price.currency.code && {
        amount: price.amount,
        currency: price.currency.code,
        url: `${SITE_DOMAIN}/bookings/tour/${tourSlug}/`,
      },
    soldOut: !!(spotsRemaining === "Sold Out"),
  }
  const rawBreadcrumbStructuredData = headerBreadcrumbs.map(
    (breadcrumb, index) => {
      return {
        name: breadcrumb.text,
        url: `${SITE_DOMAIN}${breadcrumb.link}`,
        position: index + 1,
      }
    },
  )
  // Instant Booking
  useEffect(() => {
    if (!(currency && currency?.code === "USD")) {
      return setBookingURL(`/bookings/tour/${tourSlug}/`)
    }
    const instantBookingUri = instantBookings.find(item =>
      item.uri.includes(pageUri),
    )?.bookingUri
    if (instantBookingUri) {
      setBookingURL(instantBookingUri)
    }
  }, [pageUri, currency, tourSlug])
  return (
    <>
      <PrimaryLayout pagePath={pageUri} bottomSeoData={bottomSeoData}>
        <PageHelmet
          title={normalizeTitle(pageSeo.title)}
          meta={generateMeta(pageUri, {
            ...pageSeo,
            metaRobotsNoindex: "index",
          })}
          structuredData={[
            generateBreadcrumbList(rawBreadcrumbStructuredData),
            generateProduct(rawStructuredData),
          ]}
        />
        <HotjarScript />
        <HappinessGuarantee
          handleClose={handleHPClose}
          className={showHP ? "" : "hidden"}
        />
        <GroupTrips
          handleClose={handleGTClose}
          openForm={() => setIsGroupTripOpen(true)}
          className={showGT ? "" : "hidden"}
        />
        {AskQuestionModal}
        {GroupTripModal}
        <StickyBookingPriceCTA price={price} bookingURL={bookingURL} />
        <ResponsiveSection>
          <BreadcrumbsV2 breadcrumbItems={headerBreadcrumbs} />
        </ResponsiveSection>
        <ResponsiveSection>
          <Header
            price={price}
            days={revisedTourIcons.find(item => item.icon === "time2")?.label}
            bookingURL={bookingURL}
          />
        </ResponsiveSection>

        <AboveFold>
          <Column>
            <SingleTourHeader
              img={featureImage}
              gallery={images.gallery}
              mediaPartners={mediaPartners}
              spotsRemaining={spotsRemaining}
            />
            <TourDescriptionHolder>
              <TourDescription
                description={seoText}
                lifetime={!!terms?.lifetime}
              />
            </TourDescriptionHolder>
          </Column>
          <InfoSideMenu
            location={ancestors}
            handleHPClick={handleHPClick}
            title={title}
            subTitle={tourHighlights.subtitle || tourHighlights.title}
            reviews={{
              rating: reviews.overall,
              total: reviews.numReviews?.total,
            }}
            tourIcons={revisedTourIcons}
            price={price}
            img={featureImage}
            days={revisedTourIcons.find(item => item.icon === "time2")?.label}
            description={seoText}
            bookingURL={bookingURL}
            setIsQuestionOpen={setIsQuestionOpen}
          />
          <InfoSideMenuPart2
            img={featureImage}
            description={seoText}
            spotsRemaining={spotsRemaining}
          />
        </AboveFold>
        <ResponsiveSection>
          <TripHighlights tourHighlights={tourHighlights} />
        </ResponsiveSection>
        <ResponsiveSection>
          <WeAre type={country} setIsQuestionOpen={setIsQuestionOpen} />
        </ResponsiveSection>
        <ResponsiveSectionDark>
          <MapAndTravellers map={map} />
        </ResponsiveSectionDark>
        <RelatedToursSection>
          <DayByDay itinerary={itinerary.days} />
        </RelatedToursSection>
        <Suspense fallback={null}>
          <WhyResponsiveSection>
            <WhyChoose
              accommodations={
                <>
                  <h3 className="green-header">{`Accommodation for ${title}`}</h3>
                  <HtmlContent
                    html={accommodationText.replace(/<h2>(.*?)<\/h2>/, "")}
                    prices={{ showAbbr: "after-full-size" }}
                  />
                  {accommodationGroups &&
                    accommodationGroups.length &&
                    accommodationGroups.map((accommodationGroup, index) => {
                      return (
                        <ReadMoreText
                          key={index}
                          seeMoreHeader={accommodationGroup.listTitle}
                          variant="fancy"
                          buttonTextObject={{
                            toOpen: { text: "See more", icon: "circle-plus" },
                            toClose: { text: "See less", icon: "circle-minus" },
                          }}
                          buttonTop
                          hideAll
                        >
                          {accommodationGroup.accommodations.map(
                            ({ hotel, nightNumber }, index) => (
                              <HotelTile
                                key={index}
                                hotel={hotel}
                                nightNumber={nightNumber}
                                lazyload={false}
                              />
                            ),
                          )}
                        </ReadMoreText>
                      )
                    })}
                </>
              }
              inclusions={tourIncluded}
            />
          </WhyResponsiveSection>
        </Suspense>
        <Suspense fallback={null}>
          <ResponsiveSection>
            <Reviews reviews={reviews} country={findCountry(tourRegion)} />
          </ResponsiveSection>
        </Suspense>

        <Suspense fallback={null}>
          <ResponsiveSection>
            <DatesAndPrices
              pricingTables={pricingTablesNew}
              priceHeader={priceHeader}
              fixedDates={fixedDates}
              dayofweekPrices={dayofweekPrices}
              priceFooter={priceFooter}
              price={price}
            />
          </ResponsiveSection>
        </Suspense>

        <Suspense fallback={null}>
          <ResponsiveSection>
            <Included included={tourIncluded} />
          </ResponsiveSection>
        </Suspense>
        <Suspense fallback={null}>
          <ResponsiveSection>
            <Accommodation
              accommodation={
                tabData.find(item => item.title === "Accommodation").content
                  .props.children
              }
            />
          </ResponsiveSection>
        </Suspense>
        {terms && terms.worse && terms.text && (
          <Suspense>
            <ResponsiveSection>
              <NonStandardCancellation terms={terms.text} />
            </ResponsiveSection>
          </Suspense>
        )}
        {faqs && faqs.length && (
          <Suspense fallback={null}>
            <ResponsiveSection>
              <FaqsSection faqs={faqs} />
            </ResponsiveSection>
          </Suspense>
        )}
        <SocialProofReasons
          groupType={groupTypes?.nodes?.length && groupTypes.nodes[0].id}
          tourType={tourType && tourType.id}
        />
      </PrimaryLayout>
    </>
  )
}

PageTemplate.propTypes = {
  pageContext: PropTypes.shape({
    id: PropTypes.number,
    pageUri: PropTypes.string,
    skipForBottomSeo: PropTypes.arrayOf(PropTypes.string),
    replaceURLs: PropTypes.object,
  }),
  data: PropTypes.shape({
    tenTours: PropTypes.shape({
      tour: PropTypes.shape({
        title: PropTypes.string,
        slug: PropTypes.string,
        spotsRemaining: PropTypes.string,
        images: PropTypes.shape({
          feature: PropTypes.shape({ ...ImagePropTypes }),
          gallery: PropTypes.arrayOf(
            PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
            }),
          ),
        }),

        map: PropTypes.shape({
          image: PropTypes.shape({
            ...ImagePropTypes,
            title: PropTypes.string,
          }),
        }),
        tourHighlights: PropTypes.shape({
          details: PropTypes.arrayOf(
            PropTypes.shape({
              details: PropTypes.string,
            }),
          ),
        }),
        seoText: PropTypes.string,
        seo: PropTypes.shape({ ...PageSeoPropTypes }),
        groupTypes: PropTypes.shape({
          nodes: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
        }),
        tourDifficulty: PropTypes.number,
        price: PropTypes.shape({
          ...PricePropTypes,
        }),
        priceHeader: PropTypes.string,
        priceFooter: PropTypes.string,
        pricingTablesNew: PropTypes.arrayOf(
          PropTypes.shape({
            tableName: PropTypes.string,
            columns: PropTypes.arrayOf(
              PropTypes.shape({
                name: PropTypes.string,
                index: PropTypes.number,
              }),
            ),
            rows: PropTypes.arrayOf(
              PropTypes.shape({
                name: PropTypes.string,
                index: PropTypes.number,
                merge: PropTypes.boolean,
                italic: PropTypes.boolean,
                blankRowBelow: PropTypes.boolean,
                dayOfWeek: PropTypes.arrayOf(PropTypes.string),
                cells: PropTypes.arrayOf(
                  PropTypes.shape({
                    index: PropTypes.number,
                    value: PropTypes.shape({
                      ...PricePropTypes,
                    }),
                  }),
                ),
              }),
            ),
          }),
        ),
        dayofweekPrices: PropTypes.shape({
          footer: PropTypes.string,
          prices: PropTypes.arrayOf(
            PropTypes.shape({
              day: PropTypes.string,
              price: PropTypes.shape({ ...PricePropTypes }),
            }),
          ),
        }),
        fixedDates: PropTypes.arrayOf(
          PropTypes.shape({
            duration: PropTypes.number,
            openings: PropTypes.number,
            start: PropTypes.shape({
              date: PropTypes.string,
              location: PropTypes.string,
            }),
            finish: PropTypes.shape({
              date: PropTypes.string,
              location: PropTypes.string,
            }),
            price: PropTypes.shape({ ...PricePropTypes }),
          }),
        ),
        tourDuration: PropTypes.shape({
          days: PropTypes.number,
          nights: PropTypes.number,
        }),
        tourIcons: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            icon: PropTypes.string,
          }),
        ),
        tourMonths: PropTypes.arrayOf(PropTypes.number),
        itinerary: PropTypes.shape({
          title: PropTypes.string,
          days: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string,
              title: PropTypes.string,
              details: PropTypes.string,
              attributes: PropTypes.arrayOf(
                PropTypes.shape({
                  key: PropTypes.string,
                  value: PropTypes.string,
                }),
              ),
            }),
          ),
        }),
        accommodationText: PropTypes.string,
        accomodationGroups: PropTypes.arrayOf(
          PropTypes.shape({
            listTitle: PropTypes.string,
            accommodations: PropTypes.shape({
              nightNumber: PropTypes.string,
              hotel: PropTypes.shape({
                ...HotelTilePropTypes,
              }),
            }),
          }),
        ),
        tourIncluded: PropTypes.string,
        faqs: PropTypes.arrayOf(
          PropTypes.shape({
            question: PropTypes.string,
            answer: PropTypes.string,
          }),
        ),
        tourOperator: PropTypes.shape({
          name: PropTypes.string,
          uri: PropTypes.string,
        }),
        tourCategory: PropTypes.shape({
          name: PropTypes.string,
          uri: PropTypes.string,
        }),
        tourRegion: PropTypes.shape({
          name: PropTypes.string,
          uri: PropTypes.string,
        }),
      }),
    }),
  }),
}

export default PageTemplate

export const query = graphql`
  query TourDetailQuery($id: ID!, $currency: TenToursGQL_CurrencyCodeEnum) {
    tenTours {
      tour(idType: DATABASE_ID, id: $id) {
        title
        slug
        spotsRemaining
        images {
          feature {
            ...TenToursGQL_ImageSizesQuery
            sizes {
              name
              src: url
              width
            }
          }
          gallery {
            alt
            src: url
            sizes {
              name
              src: url
            }
          }
        }
        map {
          image {
            ...TenToursGQL_ImageSizesQuery
            title
          }
        }
        tourHighlights {
          title
          subtitle
          details {
            details
          }
        }
        seoText
        seo {
          ...TenToursGQL_PostSEO
        }
        tourType {
          id: databaseId
        }
        groupTypes {
          nodes {
            id: databaseId
            name
          }
        }
        tourDifficulty
        price(currency: $currency) {
          ...PriceFragment
        }
        priceHeader
        priceFooter
        dayofweekPrices {
          footer
          prices {
            day
            price(currency: $currency) {
              ...PriceFragment
            }
          }
        }
        fixedDates {
          duration
          openings
          start {
            date
            location
          }
          finish {
            date
            location
          }
          price(currency: $currency) {
            ...PriceFragment
          }
        }
        pricingTablesNew(currency: $currency) {
          tableName
          columns {
            name
            index
          }
          rows {
            name
            index
            merge
            italic
            blankRowBelow
            dayOfWeek
            cells {
              index
              text
              value {
                ...PriceFragment
              }
            }
          }
        }
        tourDuration {
          days
          nights
        }
        tourIcons {
          label
          icon
        }
        itinerary(format: HTML) {
          title
          days {
            label
            title
            details
            attributes {
              key
              value
            }
          }
        }
        accommodationText
        accommodationGroups {
          listTitle
          accommodations {
            nightNumber
            hotel {
              ...TenToursGQL_HotelTileQuery
            }
          }
        }
        tourIncluded
        faqs(format: HTML) {
          question
          answer
        }
        tourTnC {
          lifetime
          text
          worse
        }
        tourOperator {
          id: databaseId
          name: title
          operatorTnC: termsConditions {
            lifetime
            text
            worse
          }
          reviews {
            overall: overall_rating
            counts {
              total
              five
              four
              three
              two
              one
            }
          }
        }
        tourCategory {
          id: databaseId
          name
          uri
          parentId: parentDatabaseId
        }
        tourMonths
        tourRegion {
          id: databaseId
          name
          slug
          uri
          regionCategories: tourCategories(where: { parent: 0 }, first: 100) {
            nodes {
              id: databaseId
              name
              slug
              uri
            }
          }
          regionParent: parent {
            node {
              name
              uri
              regionGrandparent: parent {
                node {
                  name
                  uri
                  regionParentSiblings: children(first: 100) {
                    nodes {
                      name
                      uri
                    }
                  }
                }
              }
              regionSiblings: children(first: 100) {
                nodes {
                  name
                  slug
                  uri
                  siblingRegionTourCategories: tourCategories(first: 100) {
                    nodes {
                      id: databaseId
                    }
                  }
                }
              }
            }
          }
          regionChildren: children(first: 100) {
            nodes {
              name
              uri
            }
          }
        }
      }
    }
  }
`
